import { apiClient } from "@/lib/api-client";
import type { GetPushPromptResp, PushPromptPlatform } from "@/types/pushPrompt.ts";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Whether the push prompt is due on `platform`, and whether any push reaches
 * the user, for web and native alike. Held until the prompt asks again (once
 * per web load, on each native foreground). A failed request reads as not due.
 */
export const pushPromptQuery = (platform: PushPromptPlatform) =>
  queryOptions({
    queryKey: ["pushPrompt", platform] as const,
    queryFn: async ({ signal }) =>
      (
        await apiClient.get<GetPushPromptResp>("/api/a/users/me/preferences/push-prompt", {
          params: { platform },
          signal,
        })
      ).data,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

const markNotDue = (old: GetPushPromptResp | undefined) => old && { ...old, due: false };

/**
 * Record that the prompt showed, which starts its wait before the next showing.
 * The cached answer becomes not due at once, as the server's will, so a read
 * racing the POST can't show it a second time.
 */
export const useRecordPushPromptShown = (
  platform: PushPromptPlatform,
  { onError }: { onError: (error: unknown) => void },
) => {
  const queryClient = useQueryClient();
  const { queryKey } = pushPromptQuery(platform);
  return useMutation({
    mutationFn: async () => {
      await apiClient.post("/api/a/users/me/preferences/push-prompt/shown", { platform });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey, exact: true });
      queryClient.setQueryData(queryKey, markNotDue);
    },
    onSuccess: () => {
      queryClient.setQueryData(queryKey, markNotDue);
    },
    onError,
  });
};
